import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Typography,
  Alert,
  Divider,
  FormControlLabel,
  Checkbox,
  CircularProgress,
  IconButton,
  InputAdornment,
  useTheme
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  Close as CloseIcon
} from '@mui/icons-material';
import { userProfileService } from '@/shared/services/userProfileService';

export default function ProfileEditDialog({
  open,
  onClose,
  user,
  isAdminMode = false,
  onSave = null,
  onUserUpdate = null,
  currentUser = null
}) {
  const theme = useTheme();

  // Profile fields
  const [formData, setFormData] = useState({
    email: '',
    fullname: '',
    company: '',
    department: ''
  });

  // Admin-only fields
  const [adminData, setAdminData] = useState({
    is_approved: false,
    is_admin: false
  });

  // Password fields (self-edit mode only)
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  // UI state
  const [showPassword, setShowPassword] = useState({
    current: false,
    new: false,
    confirm: false
  });
  const [changePassword, setChangePassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});

  // Reset form when dialog opens
  useEffect(() => {
    if (open && user) {
      setFormData({
        email: user.email || '',
        fullname: user.fullname || '',
        company: user.company || '',
        department: user.department || ''
      });

      if (isAdminMode) {
        setAdminData({
          is_approved: user.is_approved || false,
          is_admin: user.is_admin || false
        });
      }

      // Reset password fields and state
      setPasswordData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
      setChangePassword(false);
      setError('');
      setFieldErrors({});
    }
  }, [open, user, isAdminMode]);

  const validateForm = () => {
    const errors = {};

    // Validate email
    if (formData.email && !userProfileService.validateEmail(formData.email)) {
      errors.email = 'Invalid email format';
    }

    // Validate password if changing
    if (changePassword && !isAdminMode) {
      if (!passwordData.currentPassword) {
        errors.currentPassword = 'Current password is required';
      }
      if (!passwordData.newPassword) {
        errors.newPassword = 'New password is required';
      } else {
        const validation = userProfileService.validatePassword(passwordData.newPassword);
        if (!validation.valid) {
          errors.newPassword = validation.message;
        }
      }
      if (passwordData.newPassword !== passwordData.confirmPassword) {
        errors.confirmPassword = 'Passwords do not match';
      }
    }

    // Admin mode: validate new password if provided
    if (isAdminMode && passwordData.newPassword) {
      const validation = userProfileService.validatePassword(passwordData.newPassword);
      if (!validation.valid) {
        errors.newPassword = validation.message;
      }
    }

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setError('');

    try {
      let result;

      if (isAdminMode) {
        // Admin updating another user
        const updateData = {
          email: formData.email || null,
          fullname: formData.fullname || null,
          company: formData.company || null,
          department: formData.department || null,
          is_approved: adminData.is_approved,
          is_admin: adminData.is_admin
        };

        // Add password if provided
        if (passwordData.newPassword) {
          updateData.new_password = passwordData.newPassword;
        }

        // Remove null values
        Object.keys(updateData).forEach(key => {
          if (updateData[key] === null || updateData[key] === '') {
            delete updateData[key];
          }
        });

        result = await userProfileService.updateUserByAdmin(user.username, updateData);
      } else {
        // User updating their own profile
        const updateData = {
          email: formData.email || null,
          fullname: formData.fullname || null,
          company: formData.company || null,
          department: formData.department || null
        };

        // Remove null/empty values
        Object.keys(updateData).forEach(key => {
          if (updateData[key] === null || updateData[key] === '') {
            delete updateData[key];
          }
        });

        if (changePassword) {
          result = await userProfileService.updateMyProfileAndPassword(
            updateData,
            passwordData.currentPassword,
            passwordData.newPassword
          );
        } else {
          result = await userProfileService.updateMyProfile(updateData);
        }
      }

      if (result.success) {
        // Notify parent component of successful update
        if (onSave) {
          onSave(result.data);
        }
        if (onUserUpdate) {
          onUserUpdate(result.data);
        }
        onClose();
      } else {
        setError(result.error);
      }
    } catch (err) {
      setError('An unexpected error occurred');
      console.error('Profile update error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    // Clear field error when user starts typing
    if (fieldErrors[field]) {
      setFieldErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const handlePasswordChange = (field, value) => {
    setPasswordData(prev => ({
      ...prev,
      [field]: value
    }));
    // Clear field error when user starts typing
    if (fieldErrors[field]) {
      setFieldErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const togglePasswordVisibility = (field) => {
    setShowPassword(prev => ({
      ...prev,
      [field]: !prev[field]
    }));
  };

  return (
    <Dialog
      open={open}
      onClose={loading ? undefined : onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: theme.shape.borderRadius
        }
      }}
    >
      <DialogTitle sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        pb: theme.spacing(1),
        fontSize: theme.typography.fontSize.lg
      }}>
        {isAdminMode ? `Edit User: ${user?.username}` : 'Edit Profile'}
        <IconButton
          onClick={onClose}
          disabled={loading}
          size="small"
          sx={{
            color: theme.palette.text.secondary,
            ml: theme.spacing(2)
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: theme.spacing(2) }}>
        {error && (
          <Alert severity="error" sx={{ mb: theme.spacing(2) }}>
            {error}
          </Alert>
        )}

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: theme.spacing(2) }}>
          {/* Profile Information Section */}
          <Typography variant="subtitle2" sx={{
            fontSize: theme.typography.fontSize.md,
            fontWeight: theme.typography.fontWeight.semibold,
            color: theme.palette.text.primary
          }}>
            Profile Information
          </Typography>

          <TextField
            fullWidth
            label="Email"
            type="email"
            value={formData.email}
            onChange={(e) => handleInputChange('email', e.target.value)}
            error={!!fieldErrors.email}
            helperText={fieldErrors.email}
            disabled={loading}
            size="small"
          />

          <TextField
            fullWidth
            label="Full Name"
            value={formData.fullname}
            onChange={(e) => handleInputChange('fullname', e.target.value)}
            disabled={loading}
            size="small"
          />

          <TextField
            fullWidth
            label="Company"
            value={formData.company}
            onChange={(e) => handleInputChange('company', e.target.value)}
            disabled={loading}
            size="small"
          />

          <TextField
            fullWidth
            label="Department"
            value={formData.department}
            onChange={(e) => handleInputChange('department', e.target.value)}
            disabled={loading}
            size="small"
          />

          {/* Admin Controls (admin mode only) */}
          {isAdminMode && (
            <>
              <Divider sx={{ my: theme.spacing(1) }} />
              <Typography variant="subtitle2" sx={{
                fontSize: theme.typography.fontSize.md,
                fontWeight: theme.typography.fontWeight.semibold,
                color: theme.palette.text.primary
              }}>
                User Status
              </Typography>

              <FormControlLabel
                control={
                  <Checkbox
                    checked={adminData.is_approved}
                    onChange={(e) => setAdminData(prev => ({
                      ...prev,
                      is_approved: e.target.checked
                    }))}
                    disabled={loading || (user?.is_admin && user?.username === currentUser?.username && adminData.is_approved)}
                  />
                }
                label={
                  user?.is_admin && user?.username === currentUser?.username && adminData.is_approved
                    ? "Approved (Cannot revoke own approval as admin)"
                    : "Approved"
                }
                sx={{
                  opacity: 1,
                  '& .MuiFormControlLabel-label': {
                    fontSize: theme.typography.fontSize.base,
                    color: (user?.is_admin && user?.username === currentUser?.username && adminData.is_approved)
                      ? theme.palette.text.disabled
                      : theme.palette.text.primary
                  }
                }}
              />

              <FormControlLabel
                control={
                  <Checkbox
                    checked={adminData.is_admin}
                    onChange={(e) => setAdminData(prev => ({
                      ...prev,
                      is_admin: e.target.checked
                    }))}
                    disabled={loading || (user?.username === currentUser?.username && user?.is_admin)}
                  />
                }
                label={
                  user?.username === currentUser?.username && user?.is_admin
                    ? "Administrator (Cannot revoke own admin privileges)"
                    : "Administrator"
                }
                sx={{
                  opacity: 1,
                  '& .MuiFormControlLabel-label': {
                    fontSize: theme.typography.fontSize.base,
                    color: (user?.username === currentUser?.username && user?.is_admin)
                      ? theme.palette.text.disabled
                      : theme.palette.text.primary
                  }
                }}
              />
            </>
          )}

          {/* Password Section */}
          <Divider sx={{ my: theme.spacing(1) }} />
          <Typography variant="subtitle2" sx={{
            fontSize: theme.typography.fontSize.md,
            fontWeight: theme.typography.fontWeight.semibold,
            color: theme.palette.text.primary
          }}>
            {isAdminMode ? 'Reset Password' : 'Change Password'}
          </Typography>

          {!isAdminMode && (
            <FormControlLabel
              control={
                <Checkbox
                  checked={changePassword}
                  onChange={(e) => setChangePassword(e.target.checked)}
                  disabled={loading}
                />
              }
              label="Change password"
            />
          )}

          {!isAdminMode && (
            <>
              <TextField
                fullWidth
                label="Current Password"
                type={showPassword.current ? 'text' : 'password'}
                value={passwordData.currentPassword}
                onChange={(e) => handlePasswordChange('currentPassword', e.target.value)}
                error={!!fieldErrors.currentPassword}
                helperText={fieldErrors.currentPassword}
                disabled={loading || !changePassword}
                size="small"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => togglePasswordVisibility('current')}
                        edge="end"
                        size="small"
                        disabled={!changePassword}
                      >
                        {showPassword.current ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  )
                }}
              />

              <TextField
                fullWidth
                label="New Password"
                type={showPassword.new ? 'text' : 'password'}
                value={passwordData.newPassword}
                onChange={(e) => handlePasswordChange('newPassword', e.target.value)}
                error={!!fieldErrors.newPassword}
                helperText={fieldErrors.newPassword || 'Minimum 6 characters'}
                disabled={loading || !changePassword}
                size="small"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => togglePasswordVisibility('new')}
                        edge="end"
                        size="small"
                        disabled={!changePassword}
                      >
                        {showPassword.new ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  )
                }}
              />

              <TextField
                fullWidth
                label="Confirm New Password"
                type={showPassword.confirm ? 'text' : 'password'}
                value={passwordData.confirmPassword}
                onChange={(e) => handlePasswordChange('confirmPassword', e.target.value)}
                error={!!fieldErrors.confirmPassword}
                helperText={fieldErrors.confirmPassword}
                disabled={loading || !changePassword}
                size="small"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => togglePasswordVisibility('confirm')}
                        edge="end"
                        size="small"
                        disabled={!changePassword}
                      >
                        {showPassword.confirm ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  )
                }}
              />
            </>
          )}

          {isAdminMode && (
            <TextField
              fullWidth
              label="New Password"
              type={showPassword.new ? 'text' : 'password'}
              value={passwordData.newPassword}
              onChange={(e) => handlePasswordChange('newPassword', e.target.value)}
              error={!!fieldErrors.newPassword}
              helperText={fieldErrors.newPassword || 'Leave empty to keep current password'}
              disabled={loading}
              size="small"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => togglePasswordVisibility('new')}
                      edge="end"
                      size="small"
                    >
                      {showPassword.new ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                )
              }}
            />
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: theme.spacing(2), pt: theme.spacing(1) }}>
        <Button
          onClick={onClose}
          disabled={loading}
          size="small"
          sx={{ height: theme.dimensions.button.small }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={loading}
          size="small"
          sx={{
            height: theme.dimensions.button.small,
            minWidth: theme.spacing(10)
          }}
        >
          {loading ? (
            <CircularProgress size={16} color="inherit" />
          ) : (
            'Save Changes'
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
}